// MunchMakers live-chat widget. Vanilla TS, no framework.
// - Bootstraps/resumes a chat session (visitorId in localStorage)
// - Sends messages, renders the handoff auto-reply
// - Polls for staff ('admin') replies typed in the admin dashboard
import '../scss/chat-widget.scss';
import { buildMessagePayload, EMAIL_ERROR_TEXT } from './modules/chat-guest-email';
const STORAGE_VISITOR = 'mm_chat_visitor_id';
const STORAGE_OPEN = 'mm_chat_open';
const POLL_MS = 3000;
function lsGet(key) {
    try {
        return localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function lsSet(key, val) {
    try {
        localStorage.setItem(key, val);
    }
    catch {
        /* storage unavailable (e.g. private mode) — degrade gracefully */
    }
}
function uuid() {
    if (crypto && 'randomUUID' in crypto)
        return crypto.randomUUID();
    return 'v-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function getVisitorId() {
    let id = lsGet(STORAGE_VISITOR);
    if (!id) {
        id = uuid();
        lsSet(STORAGE_VISITOR, id);
    }
    return id;
}
class ChatWidget {
    constructor(root) {
        // Guest who has not given an email yet: the server refuses their message
        // without one, so the widget asks first (logged-in customers never see it).
        this.emailRequired = false;
        this.sessionId = null;
        this.lastAt = null;
        this.pollTimer = null;
        this.seen = new Set();
        this.root = root;
        this.render();
        if (lsGet(STORAGE_OPEN) === '1')
            void this.open();
    }
    render() {
        this.root.classList.add('mm-chat');
        this.root.innerHTML = `
      <button class="mm-chat__bubble" aria-label="Open chat">💬</button>
      <div class="mm-chat__panel" role="dialog" aria-label="Chat with MunchMakers">
        <div class="mm-chat__header">
          <h3>Chat with us</h3>
          <button class="mm-chat__close" aria-label="Minimize chat">▾</button>
        </div>
        <div class="mm-chat__messages"></div>
        <div class="mm-chat__guest" hidden>
          <input type="email" class="mm-chat__email" placeholder="Your email, so we can reply if you step away" aria-label="Your email" autocomplete="email" inputmode="email" />
          <input type="text" class="mm-chat__name" placeholder="Name (optional)" aria-label="Your name (optional)" autocomplete="name" />
          <div class="mm-chat__guest-err" role="alert"></div>
        </div>
        <form class="mm-chat__form">
          <input type="text" placeholder="Type a message…" aria-label="Message" autocomplete="off" />
          <button type="submit">Send</button>
        </form>
      </div>`;
        this.messagesEl = this.root.querySelector('.mm-chat__messages');
        this.input = this.root.querySelector('.mm-chat__form input');
        this.sendBtn = this.root.querySelector('.mm-chat__form button');
        this.guestEl = this.root.querySelector('.mm-chat__guest');
        this.emailInput = this.root.querySelector('.mm-chat__email');
        this.nameInput = this.root.querySelector('.mm-chat__name');
        this.guestErr = this.root.querySelector('.mm-chat__guest-err');
        this.emailInput.addEventListener('input', () => this.setGuestError(''));
        this.emailInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                void this.send();
            }
        });
        this.root.querySelector('.mm-chat__bubble').addEventListener('click', () => void this.open());
        this.root.querySelector('.mm-chat__close').addEventListener('click', () => this.close());
        this.root.querySelector('.mm-chat__form').addEventListener('submit', (e) => {
            e.preventDefault();
            void this.send();
        });
    }
    async open() {
        this.root.classList.add('is-open');
        lsSet(STORAGE_OPEN, '1');
        if (!this.sessionId)
            await this.bootstrap();
        if (this.sessionId)
            this.startPolling();
        (this.emailRequired && !this.emailInput.value ? this.emailInput : this.input).focus();
    }
    close() {
        this.root.classList.remove('is-open');
        lsSet(STORAGE_OPEN, '0');
        this.stopPolling();
    }
    async bootstrap() {
        try {
            const res = await fetch('/api/chat/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitorId: getVisitorId(), pageUrl: location.href }),
            });
            const data = await res.json();
            if (!data.success)
                return;
            this.sessionId = data.session.id;
            this.setEmailRequired(data.session.emailRequired === true);
            data.session.messages.forEach((m) => this.addMessage(m));
        }
        catch {
            /* offline — retry on next open */
        }
    }
    setEmailRequired(required) {
        this.emailRequired = required;
        this.guestEl.hidden = !required;
        if (!required)
            this.setGuestError('');
    }
    setGuestError(text) {
        this.guestErr.textContent = text;
        this.emailInput.classList.toggle('is-invalid', !!text);
    }
    async send() {
        if (!this.sessionId)
            return;
        const built = buildMessagePayload(this.sessionId, this.input.value, this.emailRequired, this.emailInput.value, this.nameInput.value);
        if (!built.ok) {
            if (built.error !== 'EMPTY') {
                // Keep what they typed; just point at the email field.
                this.setGuestError(EMAIL_ERROR_TEXT[built.error]);
                this.emailInput.focus();
            }
            return;
        }
        const typed = this.input.value;
        this.sendBtn.disabled = true;
        let focusEmail = false;
        try {
            const res = await fetch('/api/chat/message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(built.payload),
            });
            const data = await res.json();
            if (data.success) {
                // Clear only once the server has the message (never lose what they typed).
                if (this.input.value === typed)
                    this.input.value = '';
                if (this.emailRequired)
                    this.setEmailRequired(false);
                this.addMessage(data.visitorMessage);
                if (data.assistantMessage)
                    this.addMessage(data.assistantMessage);
            }
            else if (data.code === 'EMAIL_REQUIRED' || data.code === 'INVALID_EMAIL') {
                this.setEmailRequired(true);
                this.setGuestError(EMAIL_ERROR_TEXT[data.code]);
                focusEmail = true;
            }
        }
        catch {
            /* swallow; the message stays in the box so the visitor can retry */
        }
        finally {
            this.sendBtn.disabled = false;
            (focusEmail ? this.emailInput : this.input).focus();
        }
    }
    addMessage(m) {
        if (this.seen.has(m.id))
            return;
        this.seen.add(m.id);
        const el = document.createElement('div');
        el.className = `mm-chat__msg mm-chat__msg--${m.role}`;
        el.textContent = m.content;
        this.messagesEl.appendChild(el);
        this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
        if (!this.lastAt || m.createdAt > this.lastAt)
            this.lastAt = m.createdAt;
    }
    startPolling() {
        if (this.pollTimer != null)
            return;
        this.pollTimer = window.setInterval(() => void this.poll(), POLL_MS);
    }
    stopPolling() {
        if (this.pollTimer != null) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }
    async poll() {
        if (!this.sessionId || document.hidden)
            return;
        try {
            const qs = new URLSearchParams({ sessionId: this.sessionId });
            if (this.lastAt)
                qs.set('after', this.lastAt);
            const res = await fetch(`/api/chat/messages?${qs.toString()}`);
            const data = await res.json();
            if (data.success)
                data.messages.forEach((m) => this.addMessage(m));
        }
        catch {
            /* transient; next tick retries */
        }
    }
}
function init() {
    let root = document.getElementById('mm-chat-root');
    if (!root) {
        root = document.createElement('div');
        root.id = 'mm-chat-root';
        document.body.appendChild(root);
    }
    new ChatWidget(root);
}
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    }
    else {
        init();
    }
}
